import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { api_urls } from "../../utilities";
import {
  deleteService,
  getService,
  patchService,
  postService,
} from "../services";

function normalizeFaqList(inner) {
  if (inner == null) return [];
  if (Array.isArray(inner)) return inner;
  if (Array.isArray(inner.data)) return inner.data;
  return inner.faqs ?? inner.records ?? inner.list ?? [];
}

export const fetchFaqs = createAsyncThunk(
  "faq/fetchFaqs",
  async (_, { rejectWithValue }) => {
    try {
      const response = await getService(api_urls.faqs, {});
      const envelope = response.data ?? {};
      const code = envelope.code ?? envelope.status;
      if (typeof code === "number" && code !== 200) {
        return rejectWithValue({
          httpStatus: response.status,
          body: envelope,
        });
      }
      const inner = envelope.data ?? envelope;
      return normalizeFaqList(inner);
    } catch (error) {
      return rejectWithValue({
        httpStatus: error?.status,
        body: error?.data ?? { message: error?.message },
      });
    }
  }
);

export const createFaq = createAsyncThunk(
  "faq/createFaq",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await postService(api_urls.faqs, payload);
      const envelope = response.data ?? {};
      const code = envelope.code ?? envelope.status;
      if (typeof code === "number" && code !== 200) {
        return rejectWithValue({
          httpStatus: response.status,
          body: envelope,
        });
      }
      return envelope.data ?? envelope;
    } catch (error) {
      return rejectWithValue({
        httpStatus: error?.status,
        body: error?.data ?? { message: error?.message },
      });
    }
  }
);

export const updateFaq = createAsyncThunk(
  "faq/updateFaq",
  async ({ id, ...payload }, { rejectWithValue }) => {
    try {
      const response = await patchService(api_urls.faqById(id), payload);
      const envelope = response.data ?? {};
      const code = envelope.code ?? envelope.status;
      if (typeof code === "number" && code !== 200) {
        return rejectWithValue({
          httpStatus: response.status,
          body: envelope,
        });
      }
      return {
        id,
        data: envelope.data ?? envelope,
      };
    } catch (error) {
      return rejectWithValue({
        httpStatus: error?.status,
        body: error?.data ?? { message: error?.message },
      });
    }
  }
);

export const deleteFaqById = createAsyncThunk(
  "faq/deleteFaqById",
  async (id, { rejectWithValue }) => {
    try {
      const response = await deleteService(api_urls.faqById(id));
      const envelope = response.data ?? {};
      const code = envelope.code ?? envelope.status;
      if (typeof code === "number" && code !== 200) {
        return rejectWithValue({
          httpStatus: response.status,
          body: envelope,
        });
      }
      return { id, data: envelope.data ?? envelope };
    } catch (error) {
      return rejectWithValue({
        httpStatus: error?.status,
        body: error?.data ?? { message: error?.message },
      });
    }
  }
);

const initialState = {
  list: [],
  loading: false,
  saving: false,
  deleting: false,
};

export const faqSlice = createSlice({
  name: "faq",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchFaqs.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchFaqs.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.list = Array.isArray(payload) ? payload : [];
      })
      .addCase(fetchFaqs.rejected, (state) => {
        state.loading = false;
      })
      .addCase(createFaq.pending, (state) => {
        state.saving = true;
      })
      .addCase(createFaq.fulfilled, (state) => {
        state.saving = false;
      })
      .addCase(createFaq.rejected, (state) => {
        state.saving = false;
      })
      .addCase(updateFaq.pending, (state) => {
        state.saving = true;
      })
      .addCase(updateFaq.fulfilled, (state, { payload }) => {
        state.saving = false;
        if (payload?.id != null && payload?.data) {
          const merged = payload.data;
          state.list = state.list.map((f) =>
            String(f.id) === String(payload.id) ? { ...f, ...merged } : f
          );
        }
      })
      .addCase(updateFaq.rejected, (state) => {
        state.saving = false;
      })
      .addCase(deleteFaqById.pending, (state) => {
        state.deleting = true;
      })
      .addCase(deleteFaqById.fulfilled, (state, { payload }) => {
        state.deleting = false;
        if (payload?.id != null) {
          state.list = state.list.filter(
            (f) => String(f.id) !== String(payload.id)
          );
        }
      })
      .addCase(deleteFaqById.rejected, (state) => {
        state.deleting = false;
      });
  },
});

export default faqSlice.reducer;
